import { MarketingTipIcon } from "@gadgetinc/themes/src/icons/MarketingTipIcon";
import { marginVertical, paddingUtil } from "@gadgetinc/themes/src/utils";
import { useStyletron } from "baseui";
import React from "react";
import { Column } from "../Column";
import { FlatCard } from "../FlatCard";
import { Row } from "../Row";
import { Divider } from "../edit/editor/Divider";
import { AUTH_BOX_MAX_WIDTH, AuthBox } from "./AuthBox";
import { Breakpoint } from "./AuthLayout";
import { EmailLogin } from "./EmailLogin";
import { GitHubSignInButton, GoogleSignInButton } from "./SignInButtons";
import type { LoginPageProps } from "./login.utils";
import { FeatureRow, LoginContextProvider, StyledBold, StyledLink, StyledParagraph, StyledParagraphSmall } from "./login.utils";

const LoginPage = (props: LoginPageProps) => {
  const [_, $theme] = useStyletron();

  const pageTitle = props.presentAsSignup ? "Sign up for Gadget" : "Log in to Gadget";
  const heading = props.presentAsSignup ? "Sign up for Gadget" : "Log in to Gadget";
  const subHeading = props.presentAsSignup ? (
    <>
      Create an account or <StyledLink href="/auth/login">log in</StyledLink>
    </>
  ) : (
    <>
      Don&lsquo;t have an account? <StyledLink href="/auth/signup">Sign up</StyledLink>
    </>
  );

  return (
    <Row
      $style={{
        height: "auto",
        minHeight: "100vh",
        [Breakpoint.Large]: {
          flexDirection: "column",
        },
      }}
    >
      <LoginContextProvider {...props}>
        <AuthBox
          isPreLogin
          pageTitle={pageTitle}
          heading={heading}
          subHeading={<StyledParagraph $style={{ fontSize: "16px", lineHeight: "20px" }}>{subHeading}</StyledParagraph>}
        >
          <Column $as="article" $style={{ maxWidth: AUTH_BOX_MAX_WIDTH }} $gap={$theme.sizing.scale900}>
            <Column $as="section" $style={{ width: "100%" }} $gap={$theme.sizing.scale600}>
              <GoogleSignInButton />
              <GitHubSignInButton />
            </Column>
            <Divider
              $style={{
                ...marginVertical(0),
                borderColor: $theme.colors.primary300,
              }}
            />

            <EmailLogin />
          </Column>
        </AuthBox>

        {props.presentAsSignup && (
          <Column
            className="gridBackground"
            $style={{
              ...paddingUtil($theme.sizing.scale1600),
              alignItems: "center",
              justifyContent: "center",
              height: "auto",
              minHeight: "100vh",
              flex: 1,
              [Breakpoint.Large]: { display: "none" },
              [Breakpoint.Medium]: paddingUtil($theme.sizing.scale1600, $theme.sizing.scale1200),
            }}
          >
            <FlatCard $as="article" $spaced $style={{ height: "auto", maxWidth: "500px" }}>
              <Column $gap={$theme.sizing.scale1200}>
                <Column $as="header" $gap={$theme.sizing.scale600}>
                  <StyledParagraph>
                    “Using Gadget is at least <StyledBold>4-5x faster</StyledBold> than setting up an app manually.”
                  </StyledParagraph>
                  <StyledParagraphSmall>― Simon Barnes, Technical Director and Partner @ Made and Bound</StyledParagraphSmall>
                </Column>
                <section>
                  <FeatureRow>Serverless functions</FeatureRow>
                  <FeatureRow>Managed database</FeatureRow>
                  <FeatureRow>Instant GraphQL API</FeatureRow>
                  <FeatureRow>CRUD actions</FeatureRow>
                  <FeatureRow>Cloud file storage</FeatureRow>
                  <FeatureRow>Autogenerated JS & React Clients</FeatureRow>
                  <FeatureRow>Ecommerce platform connections</FeatureRow>
                </section>
                <Row $as="footer" $gap={$theme.sizing.scale600}>
                  <MarketingTipIcon />
                  <StyledParagraph>
                    Made by the same people who built infrastructure at <StyledBold>Shopify</StyledBold> and <StyledBold>Google</StyledBold>
                  </StyledParagraph>
                </Row>
              </Column>
            </FlatCard>
          </Column>
        )}
      </LoginContextProvider>
    </Row>
  );
};

export default LoginPage;
